// Tiny template renderer for order_templates rows.
//
// Supports two constructs:
//   {{token.path}}    -> looked up on the context object (dotted paths ok)
//   {{lines}}         -> rendered as <tr> rows from order.lines
//   {{form}}          -> the rendered client_form template, embedded
//   {{#if x}}...{{/if}} -> simple conditional on truthiness of x
//
// Intentionally minimal: no expressions, no loops other than the {{lines}}
// special case. If a token doesn't resolve, it renders as an empty string.

function getPath(obj, path) {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

function money(n) {
  if (n == null || n === '') return ''
  return '£' + Number(n).toFixed(2)
}

function renderLineRow(l) {
  const packNote = l.pack_size_at_order ? ` <span style="color:#888;">(${l.pack_size_at_order}/box)</span>` : ''
  return `
    <tr>
      <td style="padding: 8px; border-bottom: 1px solid #eee;">
        <div><strong>${escapeHtml(l.name_at_order)}</strong>${packNote}</div>
        ${l.description_at_order ? `<div style="color:#666; font-size: 12px;">${escapeHtml(l.description_at_order)}</div>` : ''}
        ${l.sku_at_order ? `<div style="color:#999; font-size: 11px;">${escapeHtml(l.sku_at_order)}</div>` : ''}
      </td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${Number(l.qty)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: left; color:#666;">${l.unit_at_order ? escapeHtml(l.unit_at_order) : ''}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${money(l.price_at_order)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${money(Number(l.price_at_order) * Number(l.qty))}</td>
    </tr>
  `
}

function renderConsignmentHeader(name, ref, primaryDark) {
  const parts = []
  if (name) parts.push(`Hamper for: <strong>${escapeHtml(name)}</strong>`)
  if (ref)  parts.push(`No: <strong>${escapeHtml(ref)}</strong>`)
  if (parts.length === 0) return ''
  const accent = primaryDark || '#444'
  return `
    <tr>
      <td colspan="5" style="padding: 12px 8px 6px; border-bottom: 2px solid ${accent};">
        <div style="color:${accent}; font-size: 13px; font-weight: 600; letter-spacing: 0.3px;">
          ${parts.join('&nbsp;&nbsp;·&nbsp;&nbsp;')}
        </div>
      </td>
    </tr>
  `
}

function renderLines(lines, brandPrimaryDark) {
  if (!lines || lines.length === 0) return ''
  // Group by consignment_id. Lines without a consignment_id are grouped together.
  const groups = new Map()
  const order = []
  for (const l of lines) {
    const key = l.consignment_id || '__none__'
    if (!groups.has(key)) {
      groups.set(key, { lines: [], recipient_name: l.recipient_name || null, recipient_ref: l.recipient_ref || null })
      order.push(key)
    }
    groups.get(key).lines.push(l)
  }
  return order.map(k => {
    const g = groups.get(k)
    const header = renderConsignmentHeader(g.recipient_name, g.recipient_ref, brandPrimaryDark)
    return header + g.lines.map(renderLineRow).join('')
  }).join('')
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}

function applyIfs(html, ctx) {
  return html.replace(/\{\{#if ([^}]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, path, body) => {
    const v = getPath(ctx, path.trim())
    return v ? body : ''
  })
}

function applyTokens(html, ctx) {
  return html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, path) => {
    const v = getPath(ctx, path)
    if (v == null) return ''
    return escapeHtml(v)
  })
}

export function buildContext({ order, company, brand, lines, formHtml }) {
  const total = (lines || []).reduce((sum, l) => sum + Number(l.price_at_order || 0) * Number(l.qty || 0), 0)
  const publicUrl = order?.public_token
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/v/${order.public_token}`
    : ''
  return {
    order: {
      reference: order?.reference || '',
      placed_at: order?.placed_at ? new Date(order.placed_at).toLocaleString() : '',
      placed_by: order?.placed_by || '',
      delivery_address: order?.delivery_address || '',
      delivery_postcode: order?.delivery_postcode || '',
      delivery_date_requested: order?.delivery_date_requested || '',
      customer_po: order?.customer_po || '',
      internal_notes: order?.internal_notes || '',
      client_notes: order?.client_notes || '',
      total: money(total),
      public_url: publicUrl,
      public_token: order?.public_token || '',
    },
    company: {
      name: company?.name || '',
      contact_name: company?.contact_name || '',
      contact_email: company?.contact_email || '',
    },
    brand: {
      name: brand?.name || '',
      primary_color: brand?.primary_color || '',
      primary_color_dark: brand?.primary_color_dark || '',
      primary_color_light: brand?.primary_color_light || '',
      email_footer: brand?.email_footer_html || '',
    },
    lines,
    formHtml,
  }
}

export function render(template, ctx) {
  if (!template) return ''
  let out = template
  // Special tokens first.
  out = out.replace(/\{\{\s*lines\s*\}\}/g, renderLines(ctx.lines, ctx.brand?.primary_color_dark))
  out = out.replace(/\{\{\s*form\s*\}\}/g, ctx.formHtml || '')
  out = out.replace(/\{\{\s*brand\.email_footer\s*\}\}/g, ctx.brand.email_footer || '')
  // Conditionals.
  out = applyIfs(out, ctx)
  // Generic tokens last.
  out = applyTokens(out, ctx)
  return out
}

export function pickTemplate(rows, kind, companyId) {
  // Prefer the company-scoped row if present; fall back to global.
  return rows.find(r => r.kind === kind && r.scope === 'company' && r.company_id === companyId)
      || rows.find(r => r.kind === kind && r.scope === 'global')
      || null
}
